import {
  Caption,
  Icon,
  IconName,
  Input,
  Separator,
  Slider,
  Stack,
  Text,
  Textarea,
} from '@eluve/components';

export type Format = 'paragraph' | 'bullet_points';
export type DetailLevel = 'concise' | 'standard' | 'detailed';

export const DEFAULT_FORMAT: Format = 'paragraph';
export const DEFAULT_DETAIL_LEVEL: DetailLevel = 'standard';

export const DETAIL_LEVEL_VALUES: DetailLevel[] = [
  'concise',
  'standard',
  'detailed',
];
const DETAIL_LEVEL_LABELS = ['CONCISE', 'DEFAULT', 'DETAILED'];

export const detailLevelToSlider = (level: DetailLevel): number =>
  DETAIL_LEVEL_VALUES.indexOf(level);

export const sliderToDetailLevel = (value: number): DetailLevel =>
  DETAIL_LEVEL_VALUES[value] ?? DEFAULT_DETAIL_LEVEL;

const SectionHeader: React.FC<{
  title: string;
  description?: string;
}> = ({ title, description }) => {
  if (!description) {
    return <Text>{title}</Text>;
  }
  return (
    <Stack gap={1}>
      <Text>{title}</Text>
      <Text size="xs" color="tertiary">
        {description}
      </Text>
    </Stack>
  );
};

const FormatCard: React.FC<{
  icon: IconName;
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ icon, label, selected, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center gap-4 rounded-lg border px-4 py-4 transition-colors ${
        selected
          ? 'border-border-secondary bg-bg-secondary'
          : 'border-border-primary bg-bg-primary hover:border-border-secondary'
      }`}
    >
      <Stack
        className="size-8 rounded-md bg-bg-quaternary"
        direction="horizontal"
        align="center"
        justify="center"
      >
        <Icon name={icon} className="text-text-primary" />
      </Stack>
      <Text inline color="secondary">
        {label}
      </Text>
    </button>
  );
};

export interface LlmOutputPreferencesFormProps {
  name?: string;
  onNameChange?: (name: string) => void;
  format: Format;
  detailLevel: DetailLevel;
  customInstructions: string;
  onFormatChange: (format: Format) => void;
  onDetailLevelChange: (detailLevel: DetailLevel) => void;
  onCustomInstructionsChange: (value: string) => void;
}

export const LlmOutputPreferencesForm = ({
  name,
  onNameChange,
  format,
  detailLevel,
  customInstructions,
  onFormatChange,
  onDetailLevelChange,
  onCustomInstructionsChange,
}: LlmOutputPreferencesFormProps) => {
  return (
    <Stack gap={6}>
      {/* Name Section */}
      {onNameChange && (
        <Stack gap={3}>
          <SectionHeader
            title="Name"
            description="Give this preference a recognizable name (optional)"
          />
          <Input
            type="text"
            placeholder="e.g., My Brief Style"
            value={name ?? ''}
            onChange={(e) => onNameChange(e.target.value)}
            maxLength={512}
          />
        </Stack>
      )}

      {/* Format Section */}
      <Stack gap={3}>
        <SectionHeader title="Format" />
        <Stack gap={2} direction="horizontal">
          <FormatCard
            icon="AlignLeft"
            label="Paragraph"
            selected={format === 'paragraph'}
            onClick={() => onFormatChange('paragraph')}
          />
          <FormatCard
            icon="List"
            label="Bullet Points"
            selected={format === 'bullet_points'}
            onClick={() => onFormatChange('bullet_points')}
          />
        </Stack>
      </Stack>

      {/* Detail Level Section */}
      <Stack gap={3}>
        <SectionHeader
          title="Detail"
          description="How detailed should the responses be?"
        />
        <Slider
          min={0}
          max={2}
          step={1}
          value={[detailLevelToSlider(detailLevel)]}
          onValueChange={(val) =>
            onDetailLevelChange(sliderToDetailLevel(val[0] ?? 0))
          }
        />
        <Stack gap={2} direction="horizontal" justify="between">
          {DETAIL_LEVEL_LABELS.map((label) => (
            <Caption
              inline
              key={label}
              size="3xs"
              weight="bold"
              color="supporting"
              className="tracking-[2px]"
            >
              {label}
            </Caption>
          ))}
        </Stack>
      </Stack>

      <Separator className="my-2" />

      {/* Custom Instructions Section */}
      <Stack gap={3}>
        <SectionHeader
          title="Custom Instructions"
          description='Any other preferences, such as "Prioritize medications over allergies," "Mention medication changes," etc.'
        />
        <Textarea
          value={customInstructions}
          onChange={(e) => onCustomInstructionsChange(e.target.value)}
          placeholder="Write your preferences here"
          rows={4}
        />
      </Stack>
    </Stack>
  );
};
